import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Chart } from "react-google-charts";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useCompany } from "../../components/context/CompanyContext";
import IndicadoresNav from "../../components/IndicadoresNav";
import Loading from "../../components/loading/loading";
import { titleSetRef } from "../../redux/titleRedux";
import { userRequest } from "../../requestMethods";
import { formatPricing } from "../../util/helper";
import { effectiveStories } from "../../util/storeScope";
import "../../App.css";

const BRAND = "#EE8253";

const BR_STATES = {
  AC: "Acre",          AL: "Alagoas",         AP: "Amapá",           AM: "Amazonas",
  BA: "Bahia",         CE: "Ceará",           DF: "Dist. Federal",   ES: "Espírito Santo",
  GO: "Goiás",         MA: "Maranhão",        MT: "Mato Grosso",     MS: "Mato Grosso do Sul",
  MG: "Minas Gerais",  PA: "Pará",            PB: "Paraíba",         PR: "Paraná",
  PE: "Pernambuco",    PI: "Piauí",           RJ: "Rio de Janeiro",  RN: "Rio Grande do Norte",
  RS: "Rio Grande do Sul", RO: "Rondônia",    RR: "Roraima",         SC: "Santa Catarina",
  SP: "São Paulo",     SE: "Sergipe",         TO: "Tocantins",
};

const MEDAL_COLORS = ["#F59E0B", "#9CA3AF", "#CD7C2F"];

export default function IndicadoresMapaVendas() {
  const [ordersGeo, setOrdersGeo] = useState([]);
  const [ordersTree, setOrdersTree] = useState([["Location", "Parent", "QuantityObj", "Percent", "Amount", "Quantity"]]);
  const [ranking, setRanking] = useState([]);
  const [totalPedidos, setTotalPedidos] = useState(0);
  const [dataIni, setDateIni] = useState(() => dayjs().add(-7, "day").format("YYYY-MM-DD"));
  const [dataFim, setDateFim] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [readonly, setReadonly] = useState("7");
  const [isLoading, setIsLoading] = useState(true);
  const [stories, setStories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [storeIds, setStoreIds] = useState([]);

  const currentUser = useSelector((state) => state.user.currentUser);
  const { companyId } = useCompany();
  const nav = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(titleSetRef({ title: "Mapa de Vendas" }));
    if (!currentUser) nav("../login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (companyId > 0 && currentUser) getStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    setStoreIds(selected.map((s) => s.value));
  }, [selected]);

  useEffect(() => {
    if (companyId > 0 && currentUser) {
      setIsLoading(true);
      carregarTudo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dataIni, dataFim, storeIds]);

  const buildObj = () => ({
    start: dataIni,
    end: dataFim,
    stories: effectiveStories(currentUser, storeIds),
  });

  const getStories = async () => {
    try {
      const res = await userRequest.get(`${companyId}/store/stories`);
      const list = res.data.map((item) => ({ label: item.store, value: item.storeId }));
      setStories(list);
      if (currentUser?.position === "Gerente") {
        const userStore = list.find((s) => s.value === currentUser.storeId);
        if (userStore) setSelected([userStore]);
      }
    } catch {}
  };

  async function carregarTudo() {
    try {
      await Promise.all([getGeomap(), getTreeMap()]);
    } finally {
      setIsLoading(false);
    }
  }

  const getTreeMap = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/heapmap`, buildObj());
      if (Array.isArray(res.data) && res.data.length > 1) setOrdersTree(res.data);
    } catch {}
  };

  const getGeomap = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/geomap`, buildObj());
      const data = Array.isArray(res.data) ? res.data : [];
      setOrdersGeo(data);
      const rows = data.slice(1);
      const parsed = rows
        .map(([location, count]) => ({
          code: String(location).replace("BR-", ""),
          count: count || 0,
        }))
        .filter((r) => r.code && r.count > 0)
        .sort((a, b) => b.count - a.count);
      setRanking(parsed);
      setTotalPedidos(parsed.reduce((s, r) => s + r.count, 0));
    } catch {}
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setReadonly(v);
    switch (v) {
      case "0":  setDateIni(dayjs().format("YYYY-MM-DD"));              setDateFim(dayjs().format("YYYY-MM-DD"));              break;
      case "7":  setDateIni(dayjs().add(-7,  "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "15": setDateIni(dayjs().add(-15, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "30": setDateIni(dayjs().add(-30, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      default: break;
    }
  };

  const periodLabel = readonly === "F" ? "filtro personalizado" : readonly === "0" ? "hoje" : `últimos ${readonly} dias`;
  const maxCount = ranking[0]?.count || 1;

  return (
    <div style={{ padding: "16px 16px 48px 4px", fontFamily: "Manrope", background: "#F9FAFB", minHeight: "100vh" }}>

      <IndicadoresNav />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Mapa de Vendas</span>
          <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8 }}>{periodLabel}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>

          {/* Pills Lojas */}
          {stories.length > 0 && (() => {
            const isGerente = currentUser?.position === "Gerente";
            return (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Lojas</div>
                <div style={{ display: "flex", gap: 6, border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
                  <label style={{ cursor: isGerente ? "default" : "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${selected.length === 0 ? BRAND : "#E5E7EB"}`, background: selected.length === 0 ? BRAND : "transparent", color: selected.length === 0 ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600 }}
                    onClick={() => !isGerente && setSelected([])}>Todas</label>
                  {stories.map((story) => {
                    const isActive = selected.some((s) => s.value === story.value);
                    return (
                      <label key={story.value}
                        style={{ cursor: isGerente ? "default" : "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${isActive ? BRAND : "#E5E7EB"}`, background: isActive ? BRAND : "transparent", color: isActive ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600, opacity: isGerente ? 0.7 : 1 }}
                        onClick={() => { if (isGerente) return; setSelected(isActive ? selected.filter((s) => s.value !== story.value) : [...selected, story]); }}>
                        {story.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Pills Período */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Período</div>
            <div style={{ display: "flex", gap: 6, border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
              {[{ v: "30", l: "30 dias" }, { v: "15", l: "15 dias" }, { v: "7", l: "7 dias" }, { v: "0", l: "Hoje" }, { v: "F", l: "Filtro" }].map(({ v, l }) => (
                <label key={v} style={{ cursor: "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${readonly === v ? BRAND : "#E5E7EB"}`, background: readonly === v ? BRAND : "transparent", color: readonly === v ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600 }}>
                  <input type="radio" value={v} checked={readonly === v} onChange={handleChange} style={{ display: "none" }} />
                  {l}
                </label>
              ))}
              {readonly === "F" && (
                <>
                  <div style={{ width: 1, background: "#E5E7EB", margin: "2px 2px" }} />
                  <input type="date" value={dataIni} onChange={(e) => setDateIni(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                  <span style={{ color: "#9CA3AF", fontSize: 12 }}>—</span>
                  <input type="date" value={dataFim} onChange={(e) => setDateFim(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                </>
              )}
            </div>
          </div>

          {/* Botão Atualizar — alinhado na base junto às pills */}
          <button
            onClick={() => { setIsLoading(true); carregarTudo(); }}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 14px", borderRadius: 8, cursor: "pointer",
              border: `1px solid ${BRAND}`, background: "#fff",
              color: BRAND, fontSize: 12, fontWeight: 700, fontFamily: "Manrope",
            }}
          >
            <RefreshIcon style={{ fontSize: 15 }} />
            Atualizar
          </button>

        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>

            {/* GeoChart — coluna esquerda, linha 1 */}
            <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>
              <div style={{ padding: "14px 18px 10px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>Distribuição por Estado</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope", marginTop: 2 }}>{totalPedidos} pedidos · {periodLabel}</div>
              </div>
              {ordersGeo.length < 2 ? (
                <div style={{ height: 380, display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF", fontSize: 13, fontFamily: "Manrope" }}>
                  Sem dados no período
                </div>
              ) : (
                <Chart
                  chartType="GeoChart"
                  width="100%"
                  height="380px"
                  data={ordersGeo}
                  options={{
                    region: "BR",
                    resolution: "provinces",
                    colorAxis: { colors: ["#FEF3ED", BRAND] },
                    backgroundColor: "#F9FAFB",
                    datalessRegionColor: "#F3F4F6",
                    defaultColor: "#F3F4F6",
                    tooltip: { isHtml: true },
                  }}
                />
              )}
            </div>

            {/* Ranking — coluna direita, span 2 linhas */}
            <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "16px 18px", gridRow: "span 2", overflowY: "auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <EmojiEventsIcon style={{ fontSize: 18, color: BRAND }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>Ranking por Estado</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope" }}>{totalPedidos} pedidos · {ranking.length} estados</div>
                </div>
              </div>
              {ranking.length === 0 ? (
                <div style={{ color: "#9CA3AF", fontSize: 13, textAlign: "center", padding: "40px 0" }}>Sem dados no período</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {ranking.slice(0, 20).map(({ code, count }, i) => {
                    const pct = Math.round((count / maxCount) * 100);
                    const sharePct = Math.round((count / totalPedidos) * 100);
                    const stateName = BR_STATES[code] || code;
                    return (
                      <div key={code}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {i < 3 ? (
                              <div style={{ width: 22, height: 22, borderRadius: "50%", background: MEDAL_COLORS[i], display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <span style={{ fontSize: 10, fontWeight: 900, color: "#fff" }}>{i + 1}</span>
                              </div>
                            ) : (
                              <span style={{ width: 22, fontSize: 11, color: "#9CA3AF", fontWeight: 600, textAlign: "center" }}>{i + 1}</span>
                            )}
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>{stateName}</div>
                              <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "Manrope" }}>{code}</div>
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 13, fontWeight: 800, color: "#111827", fontFamily: "Manrope" }}>{count}</div>
                            <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "Manrope" }}>{sharePct}%</div>
                          </div>
                        </div>
                        <div style={{ height: 4, borderRadius: 2, background: "#F3F4F6", overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, borderRadius: 2, background: i === 0 ? BRAND : i < 3 ? "#3B82F6" : "#10B981", transition: "width 0.4s ease" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          {/* TreeMap — coluna esquerda, linha 2 */}
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>
            <div style={{ padding: "14px 18px 10px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>Heatmap de Vendas</div>
              <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope", marginTop: 2 }}>distribuição por estado e cidade · {periodLabel}</div>
            </div>
            {ordersTree.length < 2 ? (
              <div style={{ height: 420, display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF", fontSize: 13, fontFamily: "Manrope" }}>
                Sem dados no período
              </div>
            ) : (
              <Chart
                chartType="TreeMap"
                width="100%"
                height="420px"
                data={ordersTree}
                options={{
                  headerHeight: 15,
                  fontColor: "black",
                  showScale: true,
                  generateTooltip: (row) => {
                    const d = ordersTree[row + 1];
                    if (!d) return "";
                    return `<div style="background:#fff;border:1px solid #E5E7EB;padding:10px;border-radius:8px;font-family:Manrope;font-size:12px">
                      <strong>${d[0]}</strong><br/>
                      Pedidos: ${d[2]}<br/>
                      Total: ${formatPricing(d[4])}<br/>
                      Ticket médio: ${formatPricing(d[3])}<br/>
                      Itens: ${d[5]}
                    </div>`;
                  },
                }}
              />
            )}
          </div>

        </div>
      )}
    </div>
  );
}
